import { InvalidUrlError, putQueryParam, toUrlT } from '@execonline-inc/url';
import { Maybe } from 'maybeasy';
import { Task } from 'taskarian';
import { AppyError } from '../../../../../../Appy';
import { InvalidEmailError, MissingEmailError } from '../../../../../../EmailAddress';
import { findLinkT, MissingLinkError } from '../../../../../../LinkyLinky';
import { Link, Resource } from '../../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../../Translations';
import { SelectableLicense } from '../Types';
import { SelectableUserGroup } from '../../../../../Form/GroupInputWithSuggestions/Types';

export interface DirectoryParticipant {
  email: string;
  firstName: string;
  lastName: string;
  userGroups: SelectableUserGroup[];
  licenses: SelectableLicense[];
}

export type DirectoryParticipantResource = Resource<DirectoryParticipant>;

export interface Participant {
  email: string;
  firstName: string;
  lastName: string;
  userGroups: SelectableUserGroup[];
  licenses: SelectableLicense[];
}

export type ParticipantResource = Resource<Participant>;

export interface FormErrors {
  email?: TPlainTextKey;
  firstName?: TPlainTextKey;
  lastName?: TPlainTextKey;
}

export interface Ready {
  kind: 'ready';
  participant: Participant;
  areLicensesAuditor: boolean;
  formErrors: FormErrors;
  isExistingParticipant: Maybe<boolean>;
  initialGroupCount: Maybe<number>;
  initialLicenseCount: Maybe<number>;
}

export interface LookingForParticipant {
  kind: 'looking-for-participant';
  participant: Participant;
  areLicensesAuditor: boolean;
  formErrors: FormErrors;
  isExistingParticipant: Maybe<boolean>;
  initialGroupCount: Maybe<number>;
  initialLicenseCount: Maybe<number>;
}

export interface Creating {
  kind: 'creating';
  participant: Participant;
  areLicensesAuditor: boolean;
  formErrors: FormErrors;
  isExistingParticipant: Maybe<boolean>;
  initialGroupCount: Maybe<number>;
  initialLicenseCount: Maybe<number>;
}

export interface Confirming {
  kind: 'confirming';
  participant: Participant;
  areLicensesAuditor: boolean;
  formErrors: FormErrors;
  isExistingParticipant: Maybe<boolean>;
  initialGroupCount: Maybe<number>;
  initialLicenseCount: Maybe<number>;
}

export interface DirectoryLookupError {
  kind: 'directory-lookup-error';
  participant: Participant;
  areLicensesAuditor: boolean;
  message: TPlainTextKey;
  formErrors: FormErrors;
  isExistingParticipant: Maybe<boolean>;
  initialGroupCount: Maybe<number>;
  initialLicenseCount: Maybe<number>;
}

export interface Error {
  kind: 'error';
  participant: Participant;
  areLicensesAuditor: boolean;
  message: TPlainTextKey;
  formErrors: FormErrors;
  isExistingParticipant: Maybe<boolean>;
  initialGroupCount: Maybe<number>;
  initialLicenseCount: Maybe<number>;
}

export type ParticipantState =
  | Ready
  | LookingForParticipant
  | Creating
  | Confirming
  | DirectoryLookupError
  | Error;

export const lookingForParticipant = (
  state: Ready | DirectoryLookupError | Error,
): LookingForParticipant => {
  return {
    kind: 'looking-for-participant',
    participant: state.participant,
    areLicensesAuditor: state.areLicensesAuditor,
    formErrors: state.formErrors,
    isExistingParticipant: state.isExistingParticipant,
    initialGroupCount: state.initialGroupCount,
    initialLicenseCount: state.initialLicenseCount,
  };
};

export const creating = (state: Ready): Creating => {
  return {
    kind: 'creating',
    participant: state.participant,
    areLicensesAuditor: state.areLicensesAuditor,
    formErrors: state.formErrors,
    isExistingParticipant: state.isExistingParticipant,
    initialGroupCount: state.initialGroupCount,
    initialLicenseCount: state.initialLicenseCount,
  };
};

export const directoryLookupError = (
  state: LookingForParticipant,
  message: TPlainTextKey,
): DirectoryLookupError => {
  return {
    kind: 'directory-lookup-error',
    participant: state.participant,
    areLicensesAuditor: state.areLicensesAuditor,
    message,
    formErrors: state.formErrors,
    isExistingParticipant: state.isExistingParticipant,
    initialGroupCount: state.initialGroupCount,
    initialLicenseCount: state.initialLicenseCount,
  };
};

export const error = (state: LookingForParticipant, message: TPlainTextKey): Error => {
  return {
    kind: 'error',
    participant: state.participant,
    areLicensesAuditor: state.areLicensesAuditor,
    message,
    formErrors: state.formErrors,
    isExistingParticipant: state.isExistingParticipant,
    initialGroupCount: state.initialGroupCount,
    initialLicenseCount: state.initialLicenseCount,
  };
};

export type ParticipantError =
  | MissingLinkError
  | AppyError
  | InvalidUrlError
  | InvalidEmailError
  | MissingEmailError;

const addEmailParam =
  (email: string) =>
  (link: Link): Task<ParticipantError, Link> =>
    toUrlT(link.href)
      .map(putQueryParam('email', email))
      .map((url) => ({ ...link, href: url.href }));

export const participantDirectoryLink =
  (email: string) =>
  (links: ReadonlyArray<Link>): Task<ParticipantError, Link> =>
    Task.succeed<ParticipantError, ReadonlyArray<Link>>(links)
      .andThen(findLinkT('v2-participant-directory'))
      .andThen(addEmailParam(email));
